import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { 
  Play, 
  ArrowRight,
  CheckCircle,
  Video,
  Send,
  Globe,
  Users,
  Zap,
  DollarSign,
  Target,
  Mail,
  MessageSquare,
  Tv,
  ShoppingBag,
  Shield,
  Fingerprint,
  Link2,
  CreditCard,
  Package,
  BadgeCheck,
  Upload,
  Sparkles
} from "lucide-react";

const mainFeatures = [
  {
    icon: <Shield className="w-8 h-8" />,
    title: "SelleVerify & Authenticate",
    description: "Our SelleVerify authentication and fingerprinting system applies detection and blockchain technology with MicroVerify. Verify luxury items, certify authenticity, and add SelleCertify badges to build buyer trust.",
    cta: "Verify Items",
    path: "/selleverify",
    color: "from-accent-success to-emerald-600",
    badges: ["🔐 SellePrint", "⛓️ Blockchain", "✓ MicroVerify"]
  },
  {
    icon: <CreditCard className="w-8 h-8" />,
    title: "Single Click Buy & Pay Later",
    description: "Enable instant purchases with single-click buy buttons. Offer flexible payment options including Buy Now, Pay Later through Klarna, PayPal, Stripe, Apple Pay, and cryptocurrency. Maximize conversions.",
    cta: "Setup Payments",
    path: "/broadcast",
    color: "from-primary to-purple-600",
    badges: ["💳 One-Click", "🕐 Pay Later", "₿ Crypto"]
  },
  {
    icon: <Package className="w-8 h-8" />,
    title: "Ship & SelleCertify",
    description: "White-label shipping with ShipStation integration. Add markup, fraud analytics, and SelleCertify seal of authenticity. Items sold are recorded on blockchain with full audit trail and certification.",
    cta: "Ship Products",
    path: "/broadcast",
    color: "from-accent-neon to-blue-600",
    badges: ["📦 ShipStation", "🛡️ Fraud Check", "📜 Certified"]
  },
  {
    icon: <Send className="w-8 h-8" />,
    title: "Broadcast to Market Endpoints",
    description: "Deploy verified items to 30+ market endpoints following each platform's rules. eBay, Amazon, social media, WhatsApp, SMS - each broadcast formatted according to the endpoint formula for maximum compliance.",
    cta: "Start Broadcasting",
    path: "/broadcast",
    color: "from-accent-warning to-orange-600",
    badges: ["🎯 Targeted", "📊 Compliant", "🌐 30+ Channels"]
  }
];

const channels = [
  { name: "WhatsApp", icon: "💬" },
  { name: "Email", icon: "✉️" },
  { name: "SMS", icon: "📱" },
  { name: "Amazon", icon: "📦" },
  { name: "eBay", icon: "🏷️" },
  { name: "YouTube", icon: "📺" },
  { name: "TikTok", icon: "🎵" },
  { name: "Instagram", icon: "📷" },
  { name: "Facebook", icon: "👤" },
  { name: "LinkedIn", icon: "💼" },
  { name: "Shopify", icon: "🛒" },
  { name: "TV Ads", icon: "📡" }
];

const pricingPlans = [
  {
    name: "SelleSpot Seller",
    price: "99",
    period: "/month",
    description: "For single product sellers & quick listings",
    features: [
      "10 broadcasts per month",
      "100 client contacts",
      "Social media channels",
      "Email & SMS broadcast",
      "1 photo per listing",
      "Price overlay included",
      "Basic analytics"
    ],
    cta: "Start Selling",
    popular: false
  },
  {
    name: "Seller Pro",
    price: "199",
    period: "/month",
    description: "For active sellers with regular broadcasts",
    features: [
      "50 broadcasts per month",
      "500 client contacts",
      "All 30+ channels",
      "Up to 3 photos per listing",
      "AI voiceover included",
      "Priority support",
      "Advanced analytics",
      "Buy Now button"
    ],
    cta: "Get Started",
    popular: true
  },
  {
    name: "SelleStudio Seller",
    price: "299",
    period: "/month",
    description: "For professional sellers & full production",
    features: [
      "Unlimited broadcasts",
      "2,000 client contacts",
      "Up to 10 photos per listing",
      "Custom voiceover & music",
      "Full production suite",
      "Dedicated support",
      "API access",
      "TV/Streaming available*"
    ],
    cta: "Go Professional",
    popular: false,
    note: "*TV/Streaming ads available at additional cost"
  }
];

const stats = [
  { value: "30+", label: "Channels" },
  { value: "1M+", label: "Reach" },
  { value: "98%", label: "Delivery" }
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div className="min-h-screen bg-background overflow-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-accent-neon flex items-center justify-center">
              <Play className="w-5 h-5 text-white fill-white" />
            </div>
            <span className="font-heading font-bold text-xl text-text-primary">SelleCast</span>
          </div>
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              className="text-text-secondary hover:text-text-primary hidden md:flex"
              onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Pricing
            </Button>
            <Button 
              variant="ghost" 
              className="text-text-secondary hover:text-text-primary"
              onClick={() => navigate("/login")}
            >
              Sign In
            </Button>
            <Button 
              className="bg-primary hover:bg-primary-hover text-white"
              onClick={() => navigate("/signup")}
            >
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-28 pb-12 px-6">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent-neon/5" />
        
        <div className="max-w-6xl mx-auto relative text-center">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Instant Sales Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-warning/10 border border-accent-warning/30 mb-6"
            >
              <Zap className="w-4 h-4 text-accent-warning" />
              <span className="text-accent-warning text-sm font-medium">Instant Sales Within Minutes</span>
              <DollarSign className="w-4 h-4 text-accent-warning" />
            </motion.div>

            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-tight mb-6">
              <span className="text-text-primary">Sell Your Products & Services</span>
              <br />
              <span className="gradient-text">Instantly to Your Clients</span>
            </h1>
            
            <p className="text-lg text-text-secondary mb-6 max-w-3xl mx-auto">
              Broadcast your products and services in a <strong className="text-primary">completely new way</strong>. 
              Make a video, select your audience, and broadcast — 
              <strong className="text-accent-success"> instant sales to clients within minutes</strong>.
            </p>

            <p className="text-base text-text-secondary mb-8 max-w-2xl mx-auto">
              <strong className="text-accent-neon">Take your photo</strong>, turn it into a stunning video, 
              and broadcast it to your clients or new customers instantly.
            </p>

            {/* Simple 3-Step Process */}
            <div className="flex flex-wrap justify-center items-center gap-4 mb-8">
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="flex items-center gap-2 px-4 py-3 rounded-xl bg-primary/10 border border-primary/30"
              >
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white font-bold">1</div>
                <span className="text-text-primary font-medium">Make a Video</span>
                <Video className="w-5 h-5 text-primary" />
              </motion.div>
              <ArrowRight className="w-5 h-5 text-text-muted hidden sm:block" />
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex items-center gap-2 px-4 py-3 rounded-xl bg-accent-neon/10 border border-accent-neon/30"
              >
                <div className="w-8 h-8 rounded-full bg-accent-neon flex items-center justify-center text-white font-bold">2</div>
                <span className="text-text-primary font-medium">Select Audience</span>
                <Target className="w-5 h-5 text-accent-neon" />
              </motion.div>
              <ArrowRight className="w-5 h-5 text-text-muted hidden sm:block" />
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="flex items-center gap-2 px-4 py-3 rounded-xl bg-accent-success/10 border border-accent-success/30"
              >
                <div className="w-8 h-8 rounded-full bg-accent-success flex items-center justify-center text-white font-bold">3</div>
                <span className="text-text-primary font-medium">Broadcast & Sell</span>
                <Send className="w-5 h-5 text-accent-success" />
              </motion.div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap justify-center gap-4 mb-4">
              <Button 
                size="lg"
                className="bg-accent-warning hover:bg-accent-warning/90 text-black px-8 py-6 text-lg font-bold animate-pulse"
                onClick={() => navigate("/suite")}
              >
                <Zap className="w-5 h-5 mr-2" />
                Start Selling NOW
              </Button>
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary-hover text-white px-8 py-6 text-lg font-semibold"
                onClick={() => navigate("/suite")}
              >
                <Video className="w-5 h-5 mr-2" />
                Create with SelleSuite
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-border hover:bg-background-paper px-8 py-6 text-lg"
                onClick={() => navigate("/broadcast")}
              >
                <Send className="w-5 h-5 mr-2" />
                Broadcast Now
              </Button>
            </div>
            
            {/* Demo Button */}
            <div className="text-center mb-8">
              <Button
                variant="outline"
                className="border-accent-warning/50 text-accent-warning hover:bg-accent-warning/10"
                onClick={() => navigate("/suite?demo=true")}
              >
                <Play className="w-4 h-4 mr-2" />
                Try Free Demo - No Credit Card Required
              </Button>
            </div>

            {/* Quick Access Cards - SelleSuite */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-10 max-w-5xl mx-auto">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="p-4 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/50 hover:border-amber-500 cursor-pointer"
                onClick={() => navigate("/suite")}
              >
                <Zap className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                <p className="text-sm text-text-primary font-medium">SelleSpot</p>
                <p className="text-xs text-text-muted">Instant</p>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="p-4 rounded-xl bg-gradient-to-br from-primary/20 to-purple-500/20 border border-primary/50 hover:border-primary cursor-pointer"
                onClick={() => navigate("/suite")}
              >
                <Video className="w-6 h-6 text-primary mx-auto mb-2" />
                <p className="text-sm text-text-primary font-medium">Seller</p>
                <p className="text-xs text-text-muted">Spotlight</p>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="p-4 rounded-xl bg-gradient-to-br from-accent-success/20 to-emerald-500/20 border border-accent-success/50 hover:border-accent-success cursor-pointer"
                onClick={() => navigate("/suite")}
              >
                <Sparkles className="w-6 h-6 text-accent-success mx-auto mb-2" />
                <p className="text-sm text-text-primary font-medium">SelleStudio</p>
                <p className="text-xs text-text-muted">Full</p>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="p-4 rounded-xl bg-background-paper border border-border hover:border-accent-neon cursor-pointer"
                onClick={() => navigate("/broadcast")}
              >
                <Send className="w-6 h-6 text-accent-neon mx-auto mb-2" />
                <p className="text-sm text-text-primary font-medium">Broadcast</p>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="p-4 rounded-xl bg-background-paper border border-border hover:border-purple-500 cursor-pointer"
                onClick={() => navigate("/acquire")}
              >
                <Target className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                <p className="text-sm text-text-primary font-medium">Create Ad</p>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="p-4 rounded-xl bg-background-paper border border-border hover:border-blue-500 cursor-pointer"
                onClick={() => navigate("/acquire")}
              >
                <Users className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                <p className="text-sm text-text-primary font-medium">Get Clients</p>
              </motion.div>
            </div>

            {/* Stats */}
            <div className="flex justify-center gap-12">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  className="text-center"
                >
                  <div className="font-heading font-bold text-3xl text-primary">{stat.value}</div>
                  <div className="text-sm text-text-muted">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Features - 4 Rectangles */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-2xl text-text-primary mb-3">
              The Complete <span className="text-primary">SelleVerify</span> Ecosystem
            </h2>
            <p className="text-text-secondary">
              From authentication to sale, every step is verified and blockchain-certified
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {mainFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
              >
                <Card 
                  className="h-full bg-background-paper border-border hover:border-primary/50 transition-all duration-300 cursor-pointer group overflow-hidden"
                  onClick={() => navigate(feature.path)}
                >
                  <CardContent className="p-5 flex flex-col h-full">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center text-white mb-4`}>
                      {feature.icon}
                    </div>
                    <h3 className="font-heading font-bold text-lg text-text-primary mb-2 group-hover:text-primary transition-colors">
                      {feature.title}
                    </h3>
                    <p className="text-text-secondary text-sm leading-relaxed flex-1 mb-4">
                      {feature.description}
                    </p>
                    {/* Badges */}
                    {feature.badges && (
                      <div className="flex flex-wrap gap-1 mb-4">
                        {feature.badges.map((badge, i) => (
                          <span key={i} className="text-xs px-2 py-1 rounded bg-background-subtle text-text-muted">
                            {badge}
                          </span>
                        ))}
                      </div>
                    )}
                    <Button 
                      className="w-full bg-background-subtle hover:bg-primary hover:text-white text-text-primary transition-all group-hover:bg-primary group-hover:text-white"
                    >
                      {feature.cta}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Channels Grid */}
      <section className="py-16 px-6 bg-background-paper/50">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading font-bold text-3xl text-text-primary mb-3">
              Deploy to 30+ Channels
            </h2>
            <p className="text-text-secondary mb-10 max-w-xl mx-auto">
              Reach your customers wherever they are with our omni-channel broadcast system
            </p>
          </motion.div>

          <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
            {channels.map((channel, i) => (
              <motion.div
                key={channel.name}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.03 }}
                className="p-4 rounded-xl bg-background border border-border hover:border-primary/50 transition-all"
              >
                <div className="text-2xl mb-1">{channel.icon}</div>
                <div className="text-xs text-text-secondary">{channel.name}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="font-heading font-bold text-3xl text-text-primary mb-2">
              Sell Your Way
            </h2>
            <p className="text-accent-warning font-semibold">Instant Sales Within Minutes</p>
          </motion.div>
          
          <div className="grid md:grid-cols-5 gap-4">
            {[
              { icon: <Video className="w-6 h-6" />, text: "Make a video in seconds with AI", color: "bg-primary/20 text-primary" },
              { icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>, text: "Turn any photo into video", color: "bg-accent-neon/20 text-accent-neon" },
              { icon: <Target className="w-6 h-6" />, text: "Select your audience", color: "bg-purple-500/20 text-purple-400" },
              { icon: <Send className="w-6 h-6" />, text: "Broadcast to 30+ channels", color: "bg-accent-success/20 text-accent-success" },
              { icon: <DollarSign className="w-6 h-6" />, text: "Get instant sales", color: "bg-accent-warning/20 text-accent-warning" }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-4 rounded-xl bg-background-paper border border-border text-center"
              >
                <div className={`w-12 h-12 rounded-xl ${item.color} flex items-center justify-center mx-auto mb-3`}>
                  {item.icon}
                </div>
                <p className="text-sm text-text-primary font-medium">{item.text}</p>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <Button 
              size="lg"
              className="bg-accent-warning hover:bg-accent-warning/90 text-black font-bold px-8"
              onClick={() => navigate("/create")}
            >
              <Zap className="w-5 h-5 mr-2" />
              Make It Happen NOW
            </Button>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-6 bg-background-paper/30">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading font-bold text-3xl text-text-primary mb-3">
              Simple, Transparent Pricing
            </h2>
            <p className="text-text-secondary max-w-xl mx-auto">
              Choose the plan that fits your business. Start free, scale as you grow.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className={`h-full ${plan.popular ? 'border-primary bg-primary/5' : 'border-border bg-background-paper'} relative overflow-hidden`}>
                  {plan.popular && (
                    <div className="absolute top-0 right-0 bg-primary text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                      POPULAR
                    </div>
                  )}
                  <CardContent className="p-6 flex flex-col h-full">
                    <h3 className="font-heading font-bold text-xl text-text-primary mb-1">{plan.name}</h3>
                    <p className="text-sm text-text-muted mb-4">{plan.description}</p>
                    
                    <div className="mb-6">
                      <span className="font-heading font-black text-4xl text-text-primary">${plan.price}</span>
                      <span className="text-text-muted">{plan.period}</span>
                    </div>

                    <ul className="space-y-3 mb-6 flex-1">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-text-secondary">
                          <CheckCircle className="w-4 h-4 text-accent-success flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>

                    <Button 
                      className={`w-full ${plan.popular ? 'bg-primary hover:bg-primary-hover text-white' : 'bg-background-subtle hover:bg-primary hover:text-white text-text-primary'}`}
                      onClick={() => navigate("/dashboard")}
                    >
                      {plan.cta}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative rounded-2xl overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-accent-warning via-primary to-accent-neon" />
            <div className="relative p-12 text-center">
              <h2 className="font-heading font-bold text-3xl text-white mb-4">
                Make It Happen NOW
              </h2>
              <p className="text-white/90 mb-4 text-lg max-w-lg mx-auto">
                Sell your products and services instantly to your clients within minutes.
              </p>
              <p className="text-white/80 mb-8 max-w-lg mx-auto">
                Make a video, select your audience, and broadcast your products 
                in a completely new kind of way.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button 
                  size="lg"
                  className="bg-white text-primary hover:bg-white/90 px-8 py-6 font-bold"
                  onClick={() => navigate("/suite")}
                >
                  <Video className="w-5 h-5 mr-2" />
                  Create Video Now
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-white/50 text-white hover:bg-white/10 px-8 py-6 font-semibold"
                  onClick={() => navigate("/suite")}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Photo to Video
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-border">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-accent-neon flex items-center justify-center">
                <Play className="w-5 h-5 text-white fill-white" />
              </div>
              <span className="font-heading font-bold text-xl text-text-primary">SelleCast</span>
            </div>
            <p className="text-text-muted text-sm text-center">
              Your Broadcast Messengers for Dedicated Selling Applications
            </p>
            <div className="flex gap-4">
              <Button variant="ghost" size="sm" className="text-text-muted hover:text-text-primary">
                Privacy
              </Button>
              <Button variant="ghost" size="sm" className="text-text-muted hover:text-text-primary">
                Terms
              </Button>
              <Button variant="ghost" size="sm" className="text-text-muted hover:text-text-primary">
                Contact
              </Button>
            </div>
          </div>
          <div className="text-center mt-8 text-text-muted text-sm">
            © {new Date().getFullYear()} SelleCast. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
